import React, { createContext, useContext, useState } from "react";
import { LocalStorage } from "../helpers/Storage";
import {
  useAllEmails,
  useInboxDataQuery,
  usePerformanceUpdateDoneDataQuery,
  useDoneDataQuery,
} from "../modules/ClientCommunication/queryActions/queryActions";
import { statuses, types } from '../modules/ClientCommunication/constants/constants';
import { PropsContext } from "../types/styles.types";

const clientCommunication = createContext<any>(null);

const useClientCommunication = () => {
  const [isSelectOpen, setIsSelectOpen] = useState(false);
  const [fsmsDefaultValue, setFsmsDefaultValue] = useState("");
  const [selectedFsmsIds, setSelectedFsmsIds] = useState([]);
  const [searchValue, setSearchValue] = useState("");
  const [googleAuthCode, setGoogleAuthCode] = useState("");
  const [emailStatus, setEmailStatus] = useState({});
  const [sendModalForOtherUser, setSendModalForOtherUser] = useState(false);
  const [areYouSureSendModalOpened, setAreYouSureSendModalOpened] =
        useState(false);
  const [debouncedSearchValue, setDebouncedSearchValue] = useState("");
  const [selectedStatuses, setSelectedStatuses] = useState(statuses);
  const [selectedTypes, setSelectedTypes] = useState(types);
  const [fsmsData, setFsmsData] = useState([]);
  const [fsmsOptions, setFsmsOptions] = useState([]);
  const [selectedFsms, setSelectedFsms] = useState([]);
  const [selectedTimePeriod, setSelectedTimePeriod] = useState("all_time");
  const [updatedStatus, setUpdatedStatus] = useState("");
  const [toValue, setToValue] = useState("");
  const [toOptions, setToOptions] = useState([]);
  const [CCoptions, setCCoptions] = useState([]);
  const [showCCField, setShowCCField] = useState(false);
  const [activeTab, setActiveTab] = useState(
    () => LocalStorage.get("activeTab") || "monthlyBoard",
  );
  const [isFetchingFsms, setIsFetchingFsms] = useState(true);
  const [userEmailReport, setUserEmailReport] = useState(null);

  const handleSearch = (value: any) => setSearchValue(value);

  const {
    inboxData,
    refetchInboxData,
    inboxFetchNextPage,
    inboxHasNextPage,
    inboxIsFetching,
    inboxIsFetchingNextPage,
  } = useInboxDataQuery(selectedFsmsIds, fsmsData, activeTab, isSelectOpen, debouncedSearchValue);

  const {
    performanceUpdateDoneData,
    performanceUpdateDoneFetchNextPage,
    performanceUpdateDoneHasNextPage,
    refetchPerformanceUpdateDoneData,
    performanceUpdateDoneIsFetching,
    performanceUpdateDoneIsFetchingNextPage,
  } = usePerformanceUpdateDoneDataQuery(selectedFsmsIds, fsmsData, activeTab, isSelectOpen, debouncedSearchValue);

  const {
    doneData,
    doneFetchNextPage,
    doneHasNextPage,
    refetchDoneData,
    doneIsFetching,
    doneIsFetchingNextPage,
  } = useDoneDataQuery(selectedFsmsIds, fsmsData, activeTab, isSelectOpen, debouncedSearchValue);

  const {
    allEmailsResponse,
    refetchAllEmails,
    fetchNextAllEmailsPage,
    allEmailsIsFetchingNextPage,
    allEmailsIsFetching,
    flattenedArrayAllEmailsResponse,
  } = useAllEmails(
    activeTab,
    selectedTimePeriod,
    debouncedSearchValue,
    selectedStatuses,
    selectedTypes,
    selectedFsmsIds,
    fsmsData,
  );

  const inboxResult: any = inboxData?.pages.map(page => page.data?.results?.map((item: any) => item));

  const flattenedArrayInboxResult = [].concat.apply([], inboxResult);

  const performanceUpdateDoneDataResult: any = performanceUpdateDoneData?.pages.map(page => page.data?.results?.map((item: any) => item));

  const flattenedArrayPerformanceUpdateDoneResult = [].concat.apply([], performanceUpdateDoneDataResult);

  const doneDataResult:any = doneData?.pages.map(page => page.data?.results?.map((item:any) => item));

  const flattenedArrayDoneResult = [].concat.apply([], doneDataResult);

  return {
    isSelectOpen,
    setIsSelectOpen,
    fsmsDefaultValue,
    setFsmsDefaultValue,
    selectedFsmsIds,
    setSelectedFsmsIds,
    searchValue,
    handleSearch,
    googleAuthCode,
    setGoogleAuthCode,
    setEmailStatus,
    emailStatus,
    sendModalForOtherUser,
    setSendModalForOtherUser,
    areYouSureSendModalOpened,
    setAreYouSureSendModalOpened,
    selectedStatuses,
    setSelectedStatuses,
    selectedTypes,
    setSelectedTypes,
    selectedFsms,
    setSelectedFsms,
    selectedTimePeriod,
    setSelectedTimePeriod,
    fsmsData,
    setFsmsData,
    setFsmsOptions,
    fsmsOptions,
    updatedStatus,
    setUpdatedStatus,
    toValue,
    setToValue,
    setToOptions,
    toOptions,
    setCCoptions,
    CCoptions,
    setShowCCField,
    showCCField,
    setActiveTab,
    activeTab,
    setDebouncedSearchValue,
    setUserEmailReport,
    userEmailReport,

    inboxData,
    inboxFetchNextPage,
    refetchInboxData,
    inboxHasNextPage,
    inboxIsFetching,
    inboxIsFetchingNextPage,
    flattenedArrayInboxResult,

    performanceUpdateDoneData,
    performanceUpdateDoneFetchNextPage,
    performanceUpdateDoneHasNextPage,
    refetchPerformanceUpdateDoneData,
    performanceUpdateDoneIsFetching,
    performanceUpdateDoneIsFetchingNextPage,
    flattenedArrayPerformanceUpdateDoneResult,

    doneData,
    doneFetchNextPage,
    doneHasNextPage,
    refetchDoneData,
    doneIsFetching,
    doneIsFetchingNextPage,
    flattenedArrayDoneResult,

    allEmailsResponse,
    refetchAllEmails,
    fetchNextAllEmailsPage,
    allEmailsIsFetchingNextPage,
    allEmailsIsFetching,
    flattenedArrayAllEmailsResponse,

    isFetchingFsms,
    setIsFetchingFsms,
  };
};

export const ClientCommunicationContext: React.FC<PropsContext> = ({ children }) => {
  const clientCommunicationData = useClientCommunication();
  return (
    <clientCommunication.Provider value={clientCommunicationData}>
      {children}
    </clientCommunication.Provider>
  );
};

export const useClientCommunicationContext = () =>
  useContext(clientCommunication);
